import { useMemo } from 'react';

/**
 * @typedef {Object} Draw
 * @property {number} [max_tickets] - Maximum number of tickets available for the draw
 * @property {number} [total_tickets_sold] - Total number of tickets sold so far
 */

/**
 * @typedef {Object} DrawMetrics
 * @property {number|null} ticketsRemaining - Number of tickets still available, or null if data unavailable
 * @property {number|null} percentageRemaining - Percentage of tickets remaining (0-100), or null if data unavailable
 * @property {function(): string|null} getUrgencyColor - Returns Tailwind color class based on stock level
 * @property {boolean} isLowStock - True if 30% or fewer tickets remain
 * @property {boolean} isSoldOut - True if no tickets remain
 */

/**
 * Hook for calculating draw metrics including ticket availability and urgency indicators.
 * 
 * @param {Draw|null|undefined} draw - The draw object containing ticket information
 * @returns {DrawMetrics} Object containing calculated metrics and utility functions
 * 
 * @example
 * const { ticketsRemaining, percentageRemaining, getUrgencyColor, isLowStock, isSoldOut } = useDrawMetrics(mainDraw);
 * 
 * // Use urgency color for styling
 * const urgencyClass = getUrgencyColor(); // Returns 'text-red-500', 'text-orange-500', 'text-green-500', or null
 * 
 * // Check stock status
 * if (isSoldOut) {
 *   return <SoldOutBanner />;
 * }
 */
export default function useDrawMetrics(draw) {
  const ticketsRemaining = useMemo(() => {
    if (!draw?.max_tickets || draw?.total_tickets_sold == null) {
      return null;
    }
    return draw.max_tickets - draw.total_tickets_sold;
  }, [draw?.max_tickets, draw?.total_tickets_sold]);

  const percentageRemaining = useMemo(() => {
    if (!draw?.max_tickets || ticketsRemaining === null) {
      return null;
    }
    return (ticketsRemaining / draw.max_tickets) * 100;
  }, [draw?.max_tickets, ticketsRemaining]);

  /**
   * Returns Tailwind color classes based on the percentage of tickets remaining.
   * - Red (text-red-500): 10% or fewer tickets remaining - critical urgency
   * - Orange (text-orange-500): 11-30% tickets remaining - moderate urgency  
   * - Green (text-green-500): More than 30% tickets remaining - normal
   * 
   * @returns {string|null} Tailwind color class or null if percentage unavailable
   */
  const getUrgencyColor = () => {
    if (percentageRemaining === null) return null;
    if (percentageRemaining <= 10) return 'text-red-500';
    if (percentageRemaining <= 30) return 'text-orange-500';
    return 'text-green-500';
  };

  const isLowStock = useMemo(() => {
    if (percentageRemaining === null) return false;
    return percentageRemaining <= 30;
  }, [percentageRemaining]);

  const isSoldOut = useMemo(() => {
    if (ticketsRemaining === null) return false;
    return ticketsRemaining <= 0;
  }, [ticketsRemaining]);

  return {
    ticketsRemaining,
    percentageRemaining,
    getUrgencyColor,
    isLowStock,
    isSoldOut
  };
}
